import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, AlertCircle, ArrowRight, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { apiPatch } from "@/lib/api";

const COPY = {
    pt: {
        title: "NexusTel",
        tagline: "Cada ligação, gerida com inteligência.",
        emailLabel: "Endereço de email",
        emailPlaceholder: "nome@angolateleccom.ao",
        passwordLabel: "Palavra-passe",
        passwordPlaceholder: "Introduza a sua palavra-passe",
        submit: "Entrar na Plataforma",
        submitting: "A entrar…",
        invalid: "Credenciais inválidas. Por favor verifique e tente novamente.",
        demoTitle: "ACESSO DEMO",
        demoHint: "Toque num perfil para preencher automaticamente.",
        footer: "© 2026 NexusTel — Angola Telecom",
        toggle: "EN",
    },
    en: {
        title: "NexusTel",
        tagline: "Every connection, managed with intelligence.",
        emailLabel: "Email address",
        emailPlaceholder: "name@angolateleccom.ao",
        passwordLabel: "Password",
        passwordPlaceholder: "Enter your password",
        submit: "Sign in to Platform",
        submitting: "Signing in…",
        invalid: "Invalid credentials. Please verify and try again.",
        demoTitle: "DEMO ACCESS",
        demoHint: "Tap a role to autofill credentials.",
        footer: "© 2026 NexusTel — Angola Telecom",
        toggle: "PT",
    },
};

const DEMO_ROLES = [
    { email: "yuri@angolateleccom.ao",     dot: "#C87941", label_pt: "Super Administrador",      label_en: "Super Admin" },
    { email: "eusebio@angolateleccom.ao",  dot: "#2E7D32", label_pt: "Gestor de Operações",      label_en: "Operations Manager" },
    { email: "marta@angolateleccom.ao",    dot: "#4F46E5", label_pt: "Administrador TI",         label_en: "IT Admin" },
    { email: "joao@angolateleccom.ao",     dot: "#0EA5E9", label_pt: "Técnico de Campo",         label_en: "Field Technician" },
    { email: "ana@angolateleccom.ao",      dot: "#E11D48", label_pt: "Apoio ao Cliente",         label_en: "Customer Service" },
    { email: "paulo@angolateleccom.ao",    dot: "#A855F7", label_pt: "Gestor de Vendas",         label_en: "Sales Manager" },
];

const DEMO_PASSWORD = "NexusTel@2026";

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated } = useAuth();
    const { setLang: setAppLang } = useLanguage();
    const [lang, setLang] = useState(() => localStorage.getItem("nx_login_lang") || localStorage.getItem("nx_lang") || "pt");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const t = COPY[lang];

    React.useEffect(() => {
        if (isAuthenticated) {
            const dest = location.state?.from?.pathname || "/dashboard";
            navigate(dest, { replace: true });
        }
    }, [isAuthenticated, navigate, location.state]);

    const switchLang = () => {
        const next = lang === "pt" ? "en" : "pt";
        setLang(next);
        localStorage.setItem("nx_login_lang", next);
    };

    const fillDemo = (role) => {
        setEmail(role.email);
        setPassword(DEMO_PASSWORD);
        setError("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const u = await login(email.trim().toLowerCase(), password);
            // The locale the user just picked on the login card wins over any stale
            // server-side preference. Persist it server-side and to localStorage so
            // every authenticated surface (UI chrome + locale-aware AI prompts)
            // lands in the chosen language.
            const desiredLang = lang;
            try {
                if (u && u.preferred_language !== desiredLang) {
                    await apiPatch("/auth/me", { preferred_language: desiredLang });
                }
            } catch {
                /* network blip — still flip the UI locally */
            }
            try {
                localStorage.setItem("nx_lang", desiredLang);
            } catch {
                /* ignore */
            }
            const dest = location.state?.from?.pathname || "/dashboard";
            // setAppLang triggers the copper fade-out + reload, which then routes
            // through the authenticated bootstrap and lands on the dashboard.
            // We still call navigate() as a safety net in case applyLang short-circuits
            // (e.g. the chosen lang already matches the current Context lang).
            navigate(dest, { replace: true });
            setAppLang(desiredLang);
        } catch {
            setError(t.invalid);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-8">
            <div className="login-mesh" aria-hidden />
            <div className="copper-orb" aria-hidden />
            <div className="grain-overlay" aria-hidden />

            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                className="relative z-10 w-full max-w-[480px]"
            >
                <div
                    className="glass-card p-10 relative"
                    style={{
                        background: "rgba(255,253,250,0.78)",
                        backdropFilter: "blur(28px) saturate(1.5)",
                    }}
                >
                    {/* Language toggle — top right of card */}
                    <button
                        type="button"
                        onClick={switchLang}
                        data-testid="lang-toggle"
                        className="absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-body font-medium transition-all hover:scale-105"
                        style={{
                            background: "rgba(200, 121, 65, 0.10)",
                            border: "1px solid rgba(200, 121, 65, 0.28)",
                            color: "var(--copper-dark)",
                            letterSpacing: "0.06em",
                        }}
                        aria-label={`Switch language to ${t.toggle}`}
                    >
                        <Globe size={12} strokeWidth={1.8} />
                        <span>{lang.toUpperCase()} · {t.toggle}</span>
                    </button>

                    <div className="relative z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15, duration: 0.5 }}
                            className="text-center mb-7"
                        >
                            <div className="inline-flex items-center justify-center mb-2.5">
                                <svg width="46" height="46" viewBox="0 0 32 32" fill="none">
                                    <defs>
                                        <linearGradient id="login-nx" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                                            <stop offset="0" stopColor="#E8A96A" />
                                            <stop offset="1" stopColor="#A0612E" />
                                        </linearGradient>
                                    </defs>
                                    <path
                                        d="M6 24V8L18 20V8M6 8c-1 3-1.5 6-1.5 9M22 8c1 3 1.5 6 1.5 9M3 13c-1 3-1.5 6 0 9M27 13c1 3 1.5 6 0 9"
                                        stroke="url(#login-nx)"
                                        strokeWidth="2.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <h1 className="font-display text-4xl text-[color:var(--slate-deep)] tracking-tight">
                                {t.title}
                            </h1>
                            <p className="mt-1.5 font-display italic text-sm text-[color:var(--copper-dark)]">
                                {t.tagline}
                            </p>
                        </motion.div>

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-start gap-2.5 mb-5 rounded-xl px-3.5 py-3"
                                style={{ background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.25)" }}
                                data-testid="login-error"
                            >
                                <AlertCircle size={16} className="text-[color:var(--error)] mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-[color:var(--error)] font-body">{error}</p>
                            </motion.div>
                        )}

                        <form onSubmit={submit} className="space-y-4">
                            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25, duration: 0.4 }}>
                                <GlassInput
                                    label={t.emailLabel}
                                    placeholder={t.emailPlaceholder}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    testId="login-email"
                                    prefix={<Mail size={16} strokeWidth={1.6} />}
                                />
                            </motion.div>
                            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, duration: 0.4 }}>
                                <GlassInput
                                    label={t.passwordLabel}
                                    placeholder={t.passwordPlaceholder}
                                    togglePassword
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    testId="login-password"
                                    prefix={<Lock size={16} strokeWidth={1.6} />}
                                />
                            </motion.div>

                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.4 }} className="pt-1">
                                <NxButton
                                    type="submit"
                                    variant="copper"
                                    size="lg"
                                    loading={loading}
                                    className="w-full"
                                    testId="login-submit"
                                    icon={!loading ? <ArrowRight size={16} /> : null}
                                >
                                    {loading ? t.submitting : t.submit}
                                </NxButton>
                            </motion.div>
                        </form>

                        {/* Demo Access */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.55, duration: 0.4 }}
                            className="mt-7 pt-6 border-t"
                            style={{ borderColor: "rgba(200, 121, 65, 0.18)" }}
                            data-testid="demo-access"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex-1 h-px" style={{ background: "rgba(200,121,65,0.20)" }} />
                                <span className="font-mono text-[10px] tracking-[0.22em] text-[color:var(--copper-dark)] font-medium">
                                    {t.demoTitle}
                                </span>
                                <div className="flex-1 h-px" style={{ background: "rgba(200,121,65,0.20)" }} />
                            </div>
                            <p className="text-[11px] font-body text-[color:var(--slate-light)] text-center mb-3">
                                {t.demoHint}
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {DEMO_ROLES.map((r, idx) => (
                                    <motion.button
                                        key={r.email}
                                        type="button"
                                        onClick={() => fillDemo(r)}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6 + idx * 0.04 }}
                                        whileHover={{ y: -1 }}
                                        className="text-left rounded-lg px-3 py-2.5 transition-all"
                                        style={{
                                            background: "rgba(255,247,237,0.55)",
                                            border: "1px solid rgba(200,121,65,0.18)",
                                        }}
                                        data-testid={`demo-${r.email.split("@")[0]}`}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "rgba(255,237,213,0.85)";
                                            e.currentTarget.style.borderColor = "rgba(200,121,65,0.38)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "rgba(255,247,237,0.55)";
                                            e.currentTarget.style.borderColor = "rgba(200,121,65,0.18)";
                                        }}
                                    >
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span
                                                className="inline-block rounded-full"
                                                style={{ width: 7, height: 7, background: r.dot, flexShrink: 0 }}
                                            />
                                            <span className="font-display text-[12px] text-[color:var(--slate-deep)] font-semibold leading-tight">
                                                {lang === "pt" ? r.label_pt : r.label_en}
                                            </span>
                                        </div>
                                        <div className="font-mono text-[10px] text-[color:var(--slate-light)] truncate pl-[15px]">
                                            {r.email}
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>

                <p className="text-center mt-6 text-[11px] font-body text-[color:var(--slate-light)]">
                    {t.footer}
                </p>
            </motion.div>
        </div>
    );
};

export default LoginPage;
